import { parentsUntil, getPreviousFocusableElement, getNextFocusableElement } from "./helper";

const tabsList = [];

const onKeyDownOnTab = (e) => {
    switch(e.key) {
        case 'ArrowLeft':
            selectPreviousTab(e.currentTarget);
            stopEventContinuing(e);
            break;
        case 'ArrowRight':
            selectNextTab(e.currentTarget);
            stopEventContinuing(e);
            break;
        case 'Tab':
            if (e.shiftKey) {
                goToPreviousTabLastChild(e.currentTarget);
            } else {
                tabIntoChildren(e.currentTarget, true);
            }
            stopEventContinuing(e);
            break;
        default:
            break;
    }
}

const stopEventContinuing = (e) => {
    e.preventDefault();
    e.stopPropagation();
}

const goToPreviousTabLastChild = (elem) => {
    const currentIndex = tabsList.indexOf(elem);
    if (typeof currentIndex === 'undefined') throw "No tab index found!";

    if (currentIndex > 0) {
        // We can select a previous tab so we should
        const previousTab = tabsList[currentIndex - 1];
        tabIntoChildren(previousTab, false);
    } else {
        // We are on the first tab, so we need to go to whatever previous element is
        const previousFocusable = getPreviousFocusableElement(elem);
        if (previousFocusable) {
            previousFocusable.focus();
        }
    }
}

const selectPreviousTab = (elem) => {
    const currentIndex = tabsList.indexOf(elem);
    if (typeof currentIndex === 'undefined') throw "No tab index found!";

    if (currentIndex == 0) {
        // We are on the first tab, so select the end tab
        selectTab(tabsList[tabsList.length - 1], true);
    } else {
        selectTab(tabsList[currentIndex - 1], true);
    }
}

const selectNextTab = (elem) => {
    const currentIndex = tabsList.indexOf(elem);
    if (typeof currentIndex === 'undefined') throw "No tab index found!";

    if (currentIndex < tabsList.length - 1) {
        // We are on the first tab, so select the end tab
        selectTab(tabsList[currentIndex + 1], true);
    } else {
        selectTab(tabsList[0], true);
    }
}

const selectTab = (elem, shouldSetFocus) => {
    // To ensure we correctly show active tab last, we need to hide the others and then show the new one last
    tabsList.forEach(tab => {
        if (tab != elem) {
            toggleTab(tab, false, shouldSetFocus);
        }
    });

    toggleTab(elem, true, shouldSetFocus);
}

const closeAllTabs = () => {
    tabsList.forEach(tab => {
        toggleTab(tab, false);
    });

    document.body.classList.remove('menu-transition');
    document.querySelector('header.header')?.classList.remove('is-active');
}

const toggleTab = (elem, toShow, shouldSetFocus) => {
    elem.setAttribute('aria-selected', toShow);

    const navItem = elem.parentElement?.classList.contains('nav__item') ? elem.parentElement : null;
    if (navItem) {
        navItem.classList.toggle('is-active', toShow);
    }

    if (toShow && shouldSetFocus) {
        elem.focus();
    }

    // Show the related tab content
    const tabContentId = elem.getAttribute('aria-controls');
    if (!tabContentId) {
        console.warn('No related tab content ID found');
        return;
    }

    const tabContentPanel = document.querySelector(`#${tabContentId}`);
    if (!tabContentPanel) {
        console.warn(`No ${tabContentId} tab content panel found`);
        return;
    }
    
    tabContentPanel.classList.toggle('is-active', toShow);

    if (toShow) {
        document.body.classList.add('menu-transition');
        document.querySelector('header.header')?.classList.add('is-active');
    }
}

const onClickOnTab = (e) => selectTab(e.currentTarget, false);

const tabIntoChildren = (currentTab, isFirstItem) => {
    const tabContentName = currentTab.getAttribute('aria-controls');
    const tabContent = document.querySelector(`#${tabContentName}`);
    if (tabContent == null) {
        console.error("Cannot find tab content");
        return;
    }

    // Grab all elements that could be focused
    const inputs = tabContent.querySelectorAll("select, input, textarea, button, a");
    const firstElement = inputs[0];
    const lastElement = inputs[inputs.length - 1];

    selectTab(currentTab, false);

    if (isFirstItem) {
        firstElement.focus();
    } else {
        lastElement.focus();
    }
}

const init = () => {
    const tabs = Array.from(document.querySelectorAll('.primaryNav [role=tab]'));
    tabs.forEach(tab => {
        tabsList.push(tab);
        tab.addEventListener('keydown', onKeyDownOnTab);
        tab.addEventListener('focus', (e) => selectTab(e.currentTarget, true));
        tab.addEventListener('click', onClickOnTab);
        tab.addEventListener('mouseenter', onClickOnTab);
    });

    document.querySelectorAll('header#header').forEach(nav => {
        nav.addEventListener('mouseleave', closeAllTabs);
    });

    // loop through each nav__subnav and find the last item that can be tabbled (e..g last subnav-link)
    // If its the last item and we have a tab press, then focus it on the next nav__item
    const tabSubNavs = document.querySelectorAll('.nav__subnav');
    tabSubNavs.forEach(subNav => {
        const inputs = subNav.querySelectorAll("select, input, textarea, button, a");
        const firstElement = inputs[0];
        const lastElement = inputs[inputs.length - 1];

        firstElement.addEventListener("keydown", (e) => {
            // We need to skip to the previous tab button
            if ((e.key === 'Tab' && e.shiftKey)) {
                e.preventDefault();
                switchToSiblingTab(e.currentTarget, false)
            }
        });

        lastElement.addEventListener("keydown", (e) => {
            // We need to jump to the next tab button
            if ((e.key === 'Tab' && !e.shiftKey)) {
                e.preventDefault();

                // Check if last tab, if last tab then we need to go to next tabblable element
                const currentTabBtn = getTabButtonOfContentElem(e.currentTarget);
                if (currentTabBtn == null) {
                    console.warn('No tab button found!');
                    return;
                }

                const currentIndex = tabsList.indexOf(currentTabBtn);
                if (currentIndex == tabsList.length - 1) {
                    // We are last tab
                    const nextFocusable = getNextFocusableElement(e.currentTarget);
                    if (nextFocusable) {
                        nextFocusable.focus();

                        // Close navigation
                        const event = new CustomEvent('PrimaryNavTriggerClose');
                        document.dispatchEvent(event);
                        closeAllTabs();
                    }
                }
                else {
                    switchToSiblingTab(e.currentTarget, true)
                }
            }
        });
    });

    const getTabButtonOfContentElem = (elem) => {
        const parentGroup = parentsUntil(elem, '.nav__subnav');
        if (parentGroup == null || parentGroup.length == 0) {
            console.warn('No tabparent group found!');
            return null;
        }

        const tabName = parentGroup[0].getAttribute('aria-labelledby');
        const currentTabBtn = document.querySelector(`#${tabName}`);
        if (currentTabBtn == null) {
            console.warn('No tab button found!');
            return null;
        }
        else {
            return currentTabBtn;
        }
    }

    const switchToSiblingTab = (currentElem, isNext) => {
        const currentTabBtn = getTabButtonOfContentElem(currentElem);
        if (currentTabBtn == null) {
            console.warn('No tab button found!');
            return;
        }

        if (isNext) {
            selectNextTab(currentTabBtn);
        } else {
            currentTabBtn.focus();
        }
    }
}

export {
    init,
}